import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { LeadTask, CreateTaskData } from '@/types/lead.types';
import { leadsService } from '@/services/leads.service';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface LeadTaskListProps {
  leadId: string;
}

export const LeadTaskList: React.FC<LeadTaskListProps> = ({ leadId }) => {
  const [tasks, setTasks] = useState<LeadTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateTaskData>({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
  });

  useEffect(() => {
    fetchTasks();
  }, [leadId]);

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await leadsService.getTasks(leadId);
      // API interceptor unwraps { success, data } - backend returns { tasks: [...] }
      const tasksData = response.data?.tasks || response.data || [];
      setTasks(Array.isArray(tasksData) ? tasksData : []);
    } catch (err) {
      setError('Failed to load tasks');
      console.error('Error fetching tasks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    try {
      setIsSubmitting(true);
      const response = await leadsService.addTask(leadId, formData);
      // API interceptor unwraps { success, data } - backend returns { task: {...} }
      const newTask = response.data?.task || response.data;
      if (newTask) {
        setTasks([newTask, ...tasks]);
      }
      setFormData({ title: '', description: '', due_date: '', due_time: '' });
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding task:', err);
      toast.error('Failed to add task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      setCompletingTaskId(taskId);
      await leadsService.completeTask(leadId, taskId);
      setTasks(tasks.map((task) =>
        task.id === taskId ? { ...task, is_completed: true } : task
      ));
    } catch (err) {
      console.error('Error completing task:', err);
      toast.error('Failed to complete task');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const formatDueDate = (date?: string, time?: string) => {
    if (!date) return null;
    const d = new Date(date);
    const dateStr = d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
    });
    return time ? `${dateStr} at ${time}` : dateStr;
  };

  const isOverdue = (date?: string, time?: string) => {
    if (!date) return false;
    const dueDate = new Date(date);
    if (time) {
      const [hours, minutes] = time.split(':');
      dueDate.setHours(parseInt(hours), parseInt(minutes));
    }
    return dueDate < new Date();
  };

  const incompleteTasks = tasks.filter((t) => !t.is_completed);
  const completedTasks = tasks.filter((t) => t.is_completed);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center space-y-3">
          <div className="w-8 h-8 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-sm text-secondary-500">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-secondary-600">{error}</p>
        <button
          onClick={fetchTasks}
          className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add Task Button / Form */}
      {!showAddForm ? (
        <Button
          variant="outline"
          onClick={() => setShowAddForm(true)}
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          }
        >
          Add Task
        </Button>
      ) : (
        <form onSubmit={handleSubmit} className="bg-secondary-50 rounded-lg p-4 space-y-4">
          <Input
            label="Task Title"
            placeholder="Enter task title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <Input
            label="Description (optional)"
            placeholder="Enter task description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="date"
              label="Due Date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            />
            <Input
              type="time"
              label="Due Time"
              value={formData.due_time}
              onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" isLoading={isSubmitting}>
              Add Task
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowAddForm(false);
                setFormData({ title: '', description: '', due_date: '', due_time: '' });
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Incomplete Tasks */}
      {incompleteTasks.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-secondary-700 mb-3">
            To Do ({incompleteTasks.length})
          </h4>
          <ul className="space-y-3">
            {incompleteTasks.map((task) => (
              <li
                key={task.id}
                className="bg-white border border-secondary-200 rounded-lg p-4"
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => handleCompleteTask(task.id)}
                    disabled={completingTaskId === task.id}
                    className="mt-0.5 flex-shrink-0 w-5 h-5 border-2 border-secondary-300 rounded hover:border-primary-500 transition-colors disabled:opacity-50"
                    aria-label="Complete task"
                  >
                    {completingTaskId === task.id && (
                      <svg className="w-full h-full text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-secondary-900">{task.title}</p>
                    {task.description && (
                      <p className="text-sm text-secondary-500 mt-1">{task.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      {task.due_date && (
                        <span
                          className={`inline-flex items-center text-xs ${
                            isOverdue(task.due_date, task.due_time)
                              ? 'text-red-600'
                              : 'text-secondary-500'
                          }`}
                        >
                          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDueDate(task.due_date, task.due_time)}
                        </span>
                      )}
                      {task.assigned_to && (
                        <span className="inline-flex items-center text-xs text-secondary-500">
                          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {task.assigned_to.full_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-secondary-500 mb-3">
            Completed ({completedTasks.length})
          </h4>
          <ul className="space-y-2">
            {completedTasks.map((task) => (
              <li
                key={task.id}
                className="bg-secondary-50 border border-secondary-100 rounded-lg p-4 opacity-75"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex-shrink-0 w-5 h-5 bg-green-500 rounded flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-secondary-500 line-through">{task.title}</p>
                    {task.description && (
                      <p className="text-sm text-secondary-400 mt-1 line-through">{task.description}</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Empty State */}
      {tasks.length === 0 && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-100 mb-4">
            <svg className="w-6 h-6 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <p className="text-secondary-600">No tasks yet</p>
          <p className="text-sm text-secondary-500 mt-1">Add a task to get started</p>
        </div>
      )}
    </div>
  );
};

export default LeadTaskList;
